import { useState, useEffect, useCallback } from 'react';
import moment from 'moment';
import { useSucursal } from '@/context/SucursalContext';
import { useSimpleEmpresa } from '@/hooks/useSimpleEmpresa';
import { useAuth } from '@/context/AuthContextApi';
import { useFechasEspeciales } from '@/hooks/useFechasEspeciales';
import * as citasApiService from '@/services/citasApiService';
import * as serviciosApiService from '@/services/serviciosApiService';
import * as clientesApiService from '@/services/clientesApiService';
import * as vehiculosApiService from '@/services/vehiculosApiService';
import toastService from '@/utils/toastService';
import horarioService from '@/services/horarioService';
import citasLogicService from '@/services/citasLogicService';
import { normalizeDataObject } from '../utils/normalizationUtils';

// Control global para evitar requests duplicados de servicios de citas
let globalServiciosCitasState = {
  loadingCitas: new Map(), // citaId -> Promise
  // Cache con TTL para evitar múltiples ejecuciones de fetchCitas
  fetchCache: new Map(), // key -> { promise, timestamp }
  CACHE_TTL: 2000 // 2 segundos
};

/**
 * Hook personalizado para manejo de lógica del calendario de citas
 */
export const useCalendario = () => {
  // Estados del componente
  const [currentDate, setCurrentDate] = useState(moment());
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedTime, setSelectedTime] = useState(null);
  const [showTimeSelector, setShowTimeSelector] = useState(false);
  const [clientes, setClientes] = useState([]);
  const [vehiculos, setVehiculos] = useState([]);
  const [citas, setCitas] = useState([]);
  const [servicios, setServicios] = useState([]);
  const [selectedServicios, setSelectedServicios] = useState([]);
  const [tiempoTotal, setTiempoTotal] = useState(0);
  const [horaFin, setHoraFin] = useState(null);
  const [horaFinAjustada, setHoraFinAjustada] = useState(false);
  const [loading, setLoading] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  // Contextos
  const { selectedSucursal, sucursalData } = useSucursal();
  const { user, isAdmin, clienteData } = useAuth();
  const { empresaActual, isSuperAdmin } = useSimpleEmpresa();

  // Hook para fechas especiales, festivos y horarios bloqueados
  const {
    festivos,
    fechasEspeciales,
    horariosBloqueados,
    getInfoFecha,
    getHorariosDisponibles,
    esFechaFestiva,
    esFechaEspecial,
    esHorarioBloqueado,
    loading: loadingFechasEspeciales
  } = useFechasEspeciales();

  // Cliente seleccionado
  const [selectedCliente, setSelectedCliente] = useState(
    !isAdmin && clienteData ? clienteData.id : ''
  );

  // Efecto para detectar cambios en el tamaño de la ventana
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Efecto para establecer el cliente cuando es un cliente logueado
  useEffect(() => {
    if (!isAdmin && clienteData) {
      setSelectedCliente(clienteData.id);
    }
  }, [isAdmin, clienteData]);

  // Función para obtener configuración especial usando el nuevo hook
  const getConfiguracionEspecial = useCallback(() => {
    // Retornar configuración basada en los datos del hook useFechasEspeciales
    const config = {
      festivos,
      fechasEspeciales,
      horariosBloqueados
    };
    
    
    return config;
  }, [festivos, fechasEspeciales, horariosBloqueados]);

  // Función para cargar servicios
  const fetchServicios = useCallback(async () => {
    try {
      let serviciosData = [];
      
      if (selectedSucursal) {
        serviciosData = await serviciosApiService.getServicios(selectedSucursal);
        
        serviciosData = serviciosData.map(servicio => {
          // Normalizar datos del servicio
          const normalizedServicio = normalizeDataObject(servicio, 'servicio');
          
          let precio = 0;
          
          const precioBase = normalizedServicio.precio || normalizedServicio.Precio;
          const detallesPorSucursal = normalizedServicio.detallesPorSucursal || normalizedServicio.DetallesPorSucursal;
          
          if (precioBase) {
            precio = precioBase;
          } else if (detallesPorSucursal && Array.isArray(detallesPorSucursal)) {
            const detallesSucursal = detallesPorSucursal.filter(detalle => {
              const normalizedDetalle = normalizeDataObject(detalle, 'detalleServicio');
              const sucursalId = normalizedDetalle.sucursalId || normalizedDetalle.SucursalId;
              const sucursalArray = normalizedDetalle.sucursal || normalizedDetalle.Sucursal;
              
              return (sucursalId === selectedSucursal) || 
                     (sucursalArray && Array.isArray(sucursalArray) && sucursalArray.includes(selectedSucursal));
            });
            
            if (detallesSucursal.length > 0) {
              const detallesConPrecio = detallesSucursal
                .map(d => normalizeDataObject(d, 'detalleServicio'))
                .filter(d => (d.precio || d.Precio) && (d.precio || d.Precio) > 0)
                .sort((a, b) => ((b.precio || b.Precio) || 0) - ((a.precio || a.Precio) || 0));
              
              if (detallesConPrecio.length > 0) {
                precio = detallesConPrecio[0].precio || detallesConPrecio[0].Precio;
              }
            }
          }
          
          return {
            ...normalizedServicio,
            Precio: precio
          };
        });
      } else {
        serviciosData = await serviciosApiService.getServicios();
      }
      
      setServicios(serviciosData);
      return serviciosData;
    } catch (error) {
      console.error("Error al cargar servicios:", error);
      toastService.error("Error al cargar servicios");
      return [];
    }
  }, [selectedSucursal]);

  // Función para cargar clientes
  const fetchClientes = useCallback(async () => {
    try {
      let clientesData = [];
      
      if (isAdmin) {
        // Usar API multi-empresa con filtrado por empresa actual
        if (empresaActual?.id) {
          clientesData = await clientesApiService.getClientes(empresaActual.id);
        }
      } else if (clienteData) {
        clientesData = [clienteData];
      }
      
      // Normalizar datos de clientes
      const normalizedClientesData = clientesData.map(cliente => 
        normalizeDataObject(cliente, 'cliente')
      );
      
      setClientes(normalizedClientesData);
      return normalizedClientesData;
    } catch (error) {
      console.error("Error al cargar clientes:", error);
      toastService.error("Error al cargar clientes");
      return [];
    }
  }, [isAdmin, clienteData, empresaActual]);

  // Función para cargar vehículos
  const fetchVehiculos = useCallback(async () => {
    try {
      let vehiculosData = [];
      
      /*console.log('🚛 [DEBUG] Cargando vehículos:', {
        isAdmin,
        clienteData: clienteData ? { id: clienteData.id } : null
      });*/
      
      if (isAdmin) {
        vehiculosData = await vehiculosApiService.getVehiculos();
        //console.log('🚛 [DEBUG] Vehículos cargados para admin:', vehiculosData.length, vehiculosData);
      } else if (clienteData && clienteData.id) {
        vehiculosData = await vehiculosApiService.getVehiculos(clienteData.id);
        //console.log('🚛 [DEBUG] Vehículos cargados para cliente:', vehiculosData.length, vehiculosData);
      }
      
      // Normalizar datos de vehículos
      const normalizedVehiculosData = vehiculosData.map(vehiculo => {
        const normalized = normalizeDataObject(vehiculo, 'vehiculo');
        /*console.log('🚛 [DEBUG] Vehículo normalizado:', {
          original: vehiculo,
          normalized: normalized
        });*/
        return normalized;
      });
      
      //console.log('🚛 [DEBUG] Estableciendo vehículos en estado:', normalizedVehiculosData.length, normalizedVehiculosData);
      setVehiculos(normalizedVehiculosData);
      return normalizedVehiculosData;
    } catch (error) {
      console.error("Error al cargar vehículos:", error);
      toastService.error("Error al cargar vehículos");
      return [];
    }
  }, [isAdmin, clienteData]);

  // Función para cargar citas
  const fetchCitas = useCallback(async (clientesData = clientes, vehiculosData = vehiculos) => {
    // Generar clave única para este tipo de request
    const cacheKey = `${isSuperAdmin ? 'super' : isAdmin ? 'admin' : 'client'}-${empresaActual?.id || 'none'}-${selectedSucursal || 'none'}-${clienteData?.id || 'none'}`;
    
    // Verificar cache con TTL
    const cached = globalServiciosCitasState.fetchCache.get(cacheKey);
    if (cached) {
      const isExpired = Date.now() - cached.timestamp > globalServiciosCitasState.CACHE_TTL;
      if (!isExpired) {
        console.log('⚠️ [useCalendario] Usando cache de fetchCitas para:', cacheKey);
        try {
          return await cached.promise;
        } catch (error) {
          // Si el cache falló, continuar con nueva ejecución
          globalServiciosCitasState.fetchCache.delete(cacheKey);
        }
      } else {
        // Cache expirado, eliminar
        globalServiciosCitasState.fetchCache.delete(cacheKey);
      }
    }

    try {
      console.log('🔄 [useCalendario] Iniciando fetchCitas para:', cacheKey);
      setLoading(true);

      // Crear la promesa principal y guardarla globalmente
      const fetchPromise = (async () => {
        let citasData;
        if (isSuperAdmin) {
          if (empresaActual?.id) {
            citasData = await citasApiService.getCitasByEmpresa(empresaActual.id);
          } else {
            setCitas([]);
            setLoading(false);
            return [];
          }
        } else if (isAdmin) {
          citasData = await citasApiService.getCitas(selectedSucursal);
        } else if (clienteData && clienteData.id) {
          citasData = await citasApiService.getCitas(selectedSucursal, clienteData.id);
        } else {
          setCitas([]);
          setLoading(false);
          return [];
        }
      
      if (!citasData || citasData.length === 0) {
        setCitas([]);
        setLoading(false);
        return [];
      }
      
      // Función para obtener servicios de una cita con deduplicación mejorada
      const obtenerServiciosCita = async (citaId) => {
        // Clave única para este servicio de cita
        const serviceCacheKey = `cita-${citaId}-servicios`;
        
        // Verificar cache con TTL para servicios individuales
        const cachedService = globalServiciosCitasState.fetchCache.get(serviceCacheKey);
        if (cachedService) {
          const isExpired = Date.now() - cachedService.timestamp > globalServiciosCitasState.CACHE_TTL;
          if (!isExpired) {
            try {
              const serviciosCita = await cachedService.promise;
              return {
                citaId,
                servicios: serviciosCita || []
              };
            } catch (error) {
              globalServiciosCitasState.fetchCache.delete(serviceCacheKey);
            }
          } else {
            globalServiciosCitasState.fetchCache.delete(serviceCacheKey);
          }
        }

        // Crear nueva promesa y guardarla en cache
        const serviciosPromise = citasApiService.getServiciosSeleccionados(citaId);
        globalServiciosCitasState.fetchCache.set(serviceCacheKey, {
          promise: serviciosPromise,
          timestamp: Date.now()
        });

        try {
          const serviciosCita = await serviciosPromise;
          return {
            citaId,
            servicios: serviciosCita || []
          };
        } catch (error) {
          console.error(`Error al obtener servicios para cita ${citaId}:`, error);
          globalServiciosCitasState.fetchCache.delete(serviceCacheKey);
          return {
            citaId,
            servicios: []
          };
        }
      };

      // Obtener servicios para todas las citas
      const promesasServicios = citasData.map(cita => obtenerServiciosCita(cita.id));
      
      const resultadosServicios = await Promise.all(promesasServicios);
      
      // Crear mapa de servicios por cita
      const mapaServicios = {};
      resultadosServicios.forEach(resultado => {
        mapaServicios[resultado.citaId] = resultado.servicios;
      });
      
      // Enriquecer las citas con datos
      const citasEnriquecidas = citasData.map(cita => {
        // Normalizar datos de la cita
        const normalizedCita = normalizeDataObject(cita, 'cita');
        
        // Extraer ID del vehículo
        let patenteId = '';
        const patente = normalizedCita.patente || normalizedCita.Patente;
        if (Array.isArray(patente) && patente.length > 0) {
          patenteId = patente[0];
        } else if (typeof patente === 'string') {
          patenteId = patente;
        }
        
        // Buscar datos del vehículo
        const vehiculoEncontrado = vehiculosData.find(v => v.id === patenteId);
        
        // Extraer ID del cliente
        let clienteId = '';
        const cliente = normalizedCita.cliente || normalizedCita.Cliente;
        if (Array.isArray(cliente) && cliente.length > 0) {
          clienteId = cliente[0];
        } else if (typeof cliente === 'string') {
          clienteId = cliente;
        } else if (vehiculoEncontrado) {
          const vehiculoCliente = vehiculoEncontrado.cliente || vehiculoEncontrado.Cliente;
          if (vehiculoCliente && Array.isArray(vehiculoCliente) && vehiculoCliente.length > 0) {
            clienteId = vehiculoCliente[0];
          }
        }
        
        // Buscar datos del cliente
        const clienteEncontrado = clientesData.find(c => c.id === clienteId);
        
        // Buscar servicios seleccionados
        const serviciosDeCita = mapaServicios[normalizedCita.id] || [];
        
        return {
          ...normalizedCita,
          clienteData: clienteEncontrado || null,
          vehiculoData: vehiculoEncontrado || null,
          ServiciosSeleccionados: serviciosDeCita,
          Servicios: serviciosDeCita,
          patenteId: patenteId,
          clienteId: clienteId
        };
      });
      
      // Filtrar citas válidas
      const citasValidas = citasEnriquecidas.filter(cita => cita.fecha || cita.Fecha);
      
        setCitas(citasValidas);
        setLoading(false);
        return citasValidas;
      })(); // Fin de la promesa interna

      // Almacenar la promesa en cache con timestamp
      globalServiciosCitasState.fetchCache.set(cacheKey, {
        promise: fetchPromise,
        timestamp: Date.now()
      });

      // Esperar resultado
      const resultado = await fetchPromise;
      return resultado;

    } catch (error) {
      console.error("Error al cargar citas:", error);
      toastService.error("Error al cargar citas");
      setLoading(false);
      // Eliminar del cache si hay error
      globalServiciosCitasState.fetchCache.delete(cacheKey);
      return [];
    }
  }, [selectedSucursal, isSuperAdmin, empresaActual, isAdmin, clienteData, clientes, vehiculos]);

  // Generar slots de tiempo
  const generateTimeSlots = useCallback(() => {
    if (!selectedDate || !sucursalData?.HorarioAtencion) {
      return [];
    }
    
    const configuracionEspecial = getConfiguracionEspecial();
    const horariosBase = horarioService.generateTimeSlots(
      selectedDate, 
      sucursalData.HorarioAtencion,
      configuracionEspecial
    );
    
    // Filtrar horarios bloqueados usando el nuevo hook
    const slotsFinales = getHorariosDisponibles(selectedDate, horariosBase);
    
    return slotsFinales;
  }, [selectedDate, sucursalData, getConfiguracionEspecial, getHorariosDisponibles]);

  // Verificar disponibilidad de slot considerando capacidad de clientes
  const isTimeSlotAvailable = useCallback((date, time) => {
    const capacidadClientes = sucursalData?.CapacidadClientes || 1;
    const duracionMinutos = tiempoTotal || 30;
    return citasLogicService.isTimeSlotAvailable(date, time, citas, capacidadClientes, duracionMinutos);
  }, [citas, sucursalData, tiempoTotal]);

  // Verificar si hay citas en un día
  const hasCitasOnDay = useCallback((day) => {
    return citasLogicService.hasCitasOnDay(day, citas, isAdmin, clienteData);
  }, [citas, isAdmin, clienteData]);

  // Obtener citas de un día
  const getCitasOnDay = useCallback((day) => {
    return citasLogicService.getCitasOnDay(day, citas, isAdmin, clienteData, servicios);
  }, [citas, isAdmin, clienteData, servicios]);

  // Obtener información de capacidad para un slot de tiempo
  const getCapacidadInfo = useCallback((date, time) => {
    if (!sucursalData) return { ocupados: 0, capacidad: 1, disponible: 1 };
    
    const capacidadClientes = sucursalData?.CapacidadClientes || 1;
    const duracionMinutos = tiempoTotal || 30;
    const ocupados = citasLogicService.contarCitasSimultaneas(date, time, citas, duracionMinutos);
    const disponible = Math.max(0, capacidadClientes - ocupados);
    
    return {
      ocupados,
      capacidad: capacidadClientes,
      disponible
    };
  }, [citas, sucursalData, tiempoTotal]);

  // Manejar selección de fecha
  const handleSelectDate = useCallback((date, isInitialLoad = false) => {
    setSelectedDate(date);
    
    const isPastDay = date.isBefore(moment(), 'day');
    
    // Verificar si el día tiene horarios configurados
    const hasSchedule = () => {
      if (!sucursalData?.HorarioAtencion) return false;
      const configuracionEspecial = getConfiguracionEspecial();
      return horarioService.fechaTieneHorarios(
        date, 
        sucursalData.HorarioAtencion,
        configuracionEspecial
      );
    };
    
    // Solo mostrar selector para fechas futuras/actuales con horarios configurados
    if (!isPastDay && hasSchedule()) {
      if (isMobile) {
        // En móvil, nunca mostrar automáticamente el selector al hacer clic en fecha
        setShowTimeSelector(false);
      } else {
        // En escritorio, siempre mostrar el selector, incluso en carga inicial
        setShowTimeSelector(true);
      }
    } else {
      setShowTimeSelector(false);
      
      if (isPastDay) {
        toastService.info("Solo puedes ver las citas de fechas pasadas, no crear nuevas");
      } else if (!hasSchedule()) {
        const nombreDia = date.locale('es').format('dddd');
        
        // Verificar si es por festivo o fecha especial
        const configuracionEspecial = getConfiguracionEspecial();
        if (configuracionEspecial) {
          const festivo = horarioService.obtenerFestivo(date, configuracionEspecial.festivos || []);
          const fechaEspecial = horarioService.obtenerFechaEspecial(date, configuracionEspecial.fechasEspeciales || []);
          
          if (festivo && festivo.comportamiento === 'cerrado') {
            const nombreFestivo = festivo.festivos_nacionales?.nombre || 
                                 festivo.festivo_nombre || 
                                 festivo.nombre || 
                                 'festivo';
            toastService.info(`El taller está cerrado por dia festivo: ${nombreFestivo}`);
          } else if (fechaEspecial && fechaEspecial.tipo === 'cierre_total') {
            toastService.info(`El taller se encuentra cerrado por fecha especial ${fechaEspecial.nombre}`);
          } else {
            toastService.info(`No hay horarios de atención configurados para ${nombreDia}`);
          }
        } else {
          toastService.info(`No hay horarios de atención configurados para ${nombreDia}`);
        }
      }
    }
  }, [isMobile, sucursalData, getConfiguracionEspecial]);

  // Manejar selección de hora
  const handleSelectTime = useCallback((time) => {
    const timeWithCorrectDate = moment(selectedDate).set({
      hour: time.hour(),
      minute: time.minute(),
      second: 0
    });
    
    setSelectedTime(timeWithCorrectDate);
    setHoraFinAjustada(false);
    
    // Calcular hora de fin si hay servicios seleccionados
    if (selectedServicios.length > 0) {
      const tiempoTotal = citasLogicService.calcularTiempoTotal(selectedServicios, servicios);
      
      // Obtener horario de atención para considerar en el cálculo
      const horarioAtencion = sucursalData?.HorarioAtencion ? 
        horarioService.parseHorarioAtencion(sucursalData.HorarioAtencion) : null;
      
      const resultado = citasLogicService.calcularHoraFin(
        timeWithCorrectDate, 
        selectedDate, 
        tiempoTotal, 
        horarioAtencion
      );
      setHoraFin(resultado.horaFin);
      setHoraFinAjustada(resultado.ajustada);
    } else {
      setHoraFin(null);
    }
  }, [selectedDate, selectedServicios, servicios, sucursalData]);

  // Manejar selección de servicios
  const handleServicioSelect = useCallback((servicioId) => {
    setSelectedServicios(prev => {
      const servicioCompleto = servicios.find(s => s.id === servicioId);
      let newSelectedServicios = [...prev];
      
      const servicioIndex = newSelectedServicios.findIndex(s => 
        typeof s === 'string' ? s === servicioId : s.id === servicioId
      );
      
      if (servicioIndex !== -1) {
        newSelectedServicios.splice(servicioIndex, 1);
      } else {
        if (servicioCompleto) {
          // Normalizar datos del servicio completo
          const normalizedServicio = normalizeDataObject(servicioCompleto, 'servicio');
          
          newSelectedServicios.push({
            id: normalizedServicio.id,
            Servicio: normalizedServicio.id,
            Tiempo: normalizedServicio.tiempoEstimado || normalizedServicio.TiempoEstimado || 0,
            Precio: normalizedServicio.precio || normalizedServicio.Precio || 0,
            Nombre: normalizedServicio.nombre || normalizedServicio.Nombre
          });
        }
      }
      
      // Calcular tiempo total
      const tiempoTotal = citasLogicService.calcularTiempoTotal(newSelectedServicios, servicios);
      setTiempoTotal(tiempoTotal);
      
      // Recalcular hora de fin si hay hora seleccionada
      if (selectedTime && tiempoTotal > 0) {
        const timeWithCorrectDate = moment(selectedDate).set({
          hour: selectedTime.hour(),
          minute: selectedTime.minute(),
          second: 0
        });
        
        // Obtener horario de atención para considerar en el cálculo
        const horarioAtencion = sucursalData?.HorarioAtencion ? 
          horarioService.parseHorarioAtencion(sucursalData.HorarioAtencion) : null;
        
        const resultado = citasLogicService.calcularHoraFin(
          timeWithCorrectDate, 
          selectedDate, 
          tiempoTotal, 
          horarioAtencion
        );
        setHoraFin(resultado.horaFin);
        setHoraFinAjustada(resultado.ajustada);
      }
      
      return newSelectedServicios;
    });
  }, [servicios, selectedTime, selectedDate, sucursalData]);

  // Manejar cambio de mes
  const handleMonthChange = useCallback((increment) => {
    setCurrentDate(prev => prev.clone().add(increment, 'month'));
  }, []);

  // Ir al día actual
  const handleGoToToday = useCallback(() => {
    const today = moment();
    setCurrentDate(today.clone());
    handleSelectDate(today, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Cerrar selector de horas
  const handleCloseTimeSelector = useCallback(() => {
    setShowTimeSelector(false);
  }, []);

  // Deseleccionar todos los servicios
  const handleDeseleccionarTodos = useCallback(() => {
    setSelectedServicios([]);
    setTiempoTotal(0);
    setHoraFin(null);
    setHoraFinAjustada(false);
  }, []);

  // Refrescar citas
  const refreshCitas = useCallback(async () => {
    try {
      setLoading(true);
      await fetchCitas();
      toastService.success('Citas actualizadas');
    } catch (error) {
      console.error('Error refrescando citas:', error);
      toastService.error('Error al actualizar citas');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Cargar datos iniciales
  useEffect(() => {
    if ((isSuperAdmin && empresaActual?.id) || (isAdmin && !isSuperAdmin) || (!isAdmin && clienteData)) {
      const loadInitialData = async () => {
        setLoading(true);
        
        try {
          const clientesData = await fetchClientes();
          const vehiculosData = await fetchVehiculos();
          await fetchCitas(clientesData, vehiculosData);
          setLoading(false);
        } catch (error) {
          console.error("Error al cargar datos iniciales:", error);
          toastService.error("Error al cargar datos");
          setLoading(false);
        }
      };
      
      loadInitialData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdmin, clienteData, isSuperAdmin, empresaActual]);

  // Cargar datos cuando cambia la sucursal
  useEffect(() => {
    if (selectedSucursal && !isSuperAdmin) {
      fetchCitas();
      fetchServicios();
      // fetchConfiguracionEspecial no longer needed - data comes from useFechasEspeciales hook
      
      const today = moment();
      setSelectedDate(today);
    } else if (isSuperAdmin && empresaActual?.id) {
      fetchCitas();
      if (selectedSucursal) {
        // fetchConfiguracionEspecial no longer needed - data comes from useFechasEspeciales hook
      }
      
      const today = moment();
      setSelectedDate(today);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedSucursal, isSuperAdmin, empresaActual]);

  // Efecto para inicializar el selector de horas cuando los datos estén listos
  useEffect(() => {
    // Solo ejecutar si tenemos los datos necesarios y una fecha seleccionada
    if (selectedDate && sucursalData?.HorarioAtencion && !loadingFechasEspeciales) {
      // Solo en desktop, mostrar automáticamente el selector para el día actual
      if (!isMobile && selectedDate.isSame(moment(), 'day')) {
        handleSelectDate(selectedDate, true);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDate, sucursalData, loadingFechasEspeciales, isMobile]);

  // Función para forzar mostrar el selector de horas (para el botón "Crear cita" en móvil)
  const handleForceShowTimeSelector = useCallback((date) => {
    setSelectedDate(date);
    
    const isPastDay = date.isBefore(moment(), 'day');
    
    // Verificar si el día tiene horarios configurados
    const hasSchedule = () => {
      if (!sucursalData?.HorarioAtencion) return false;
      const configuracionEspecial = getConfiguracionEspecial();
      return horarioService.fechaTieneHorarios(
        date, 
        sucursalData.HorarioAtencion,
        configuracionEspecial
      );
    };
    
    // Forzar mostrar selector si la fecha es válida
    if (!isPastDay && hasSchedule()) {
      setShowTimeSelector(true);
    } else {
      setShowTimeSelector(false);
      
      if (isPastDay) {
        toastService.info("Solo puedes ver las citas de fechas pasadas, no crear nuevas");
      } else if (!hasSchedule()) {
        const nombreDia = date.locale('es').format('dddd');
        
        // Verificar si es por festivo o fecha especial
        const configuracionEspecial = getConfiguracionEspecial();
        if (configuracionEspecial) {
          const festivo = horarioService.obtenerFestivo(date, configuracionEspecial.festivos || []);
          const fechaEspecial = horarioService.obtenerFechaEspecial(date, configuracionEspecial.fechasEspeciales || []);
          
          if (festivo && festivo.comportamiento === 'cerrado') {
            const nombreFestivo = festivo.festivos_nacionales?.nombre || 
                                 festivo.festivo_nombre || 
                                 festivo.nombre || 
                                 'festivo';
            toastService.info(`El taller está cerrado por ${nombreFestivo}`);
          } else if (fechaEspecial && fechaEspecial.tipo === 'cierre_total') {
            toastService.info(`El taller se encuentra cerrado por fecha especial ${fechaEspecial.nombre}`);
          } else {
            toastService.info(`No hay horarios de atención configurados para ${nombreDia}`);
          }
        } else {
          toastService.info(`No hay horarios de atención configurados para ${nombreDia}`);
        }
      }
    }
  }, [sucursalData, getConfiguracionEspecial]);

  return {
    // Estados
    currentDate,
    selectedDate,
    selectedTime,
    showTimeSelector,
    clientes,
    vehiculos,
    citas,
    servicios,
    selectedServicios,
    tiempoTotal,
    horaFin,
    horaFinAjustada,
    loading: loading || loadingFechasEspeciales,
    setLoading,
    isMobile,
    selectedCliente,
    setSelectedCliente,
    
    // Funciones
    generateTimeSlots,
    isTimeSlotAvailable,
    hasCitasOnDay,
    getCitasOnDay,
    getCapacidadInfo,
    handleSelectDate,
    handleSelectTime,
    handleServicioSelect,
    handleMonthChange,
    handleGoToToday,
    handleCloseTimeSelector,
    handleDeseleccionarTodos,
    handleForceShowTimeSelector,
    refreshCitas,
    fetchServicios,
    getConfiguracionEspecial,
    
    // Funciones del hook de fechas especiales
    getInfoFecha,
    esFechaFestiva,
    esFechaEspecial,
    esHorarioBloqueado,
    
    // Datos de contexto
    selectedSucursal,
    sucursalData,
    isAdmin,
    isSuperAdmin,
    clienteData,
    user,
    empresaActual
  };
};